/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "react-toastify";
import { Search, Trash2, Pencil, Users, ChevronRight, Shield } from "lucide-react";
import { useTeams, useDeleteTeam, useUpdateTeam } from "@/hooks/api/useTeams";
import TablePagination from "@/components/TablePagination";
import TeamDetailDrawer from "@/pages/teams/TeamDetailDrawer";
import TeamEditModal from "@/pages/teams/TeamEditModal";
import { AdminTeam } from "@/types/api.types";
import type { TeamFilters } from "@/api/services/teams.service";

export default function TeamManagement() {
  const [filters, setFilters] = useState<TeamFilters>({ page: 1, limit: 20 });
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [editingTeam, setEditingTeam] = useState<AdminTeam | null>(null);

  const { data, isLoading } = useTeams(filters);
  const teams = data?.teams || [];
  const pagination = data?.pagination;
  const { mutate: deleteTeam } = useDeleteTeam();
  const { mutate: updateTeam, isPending: isUpdating } = useUpdateTeam();
  const search = filters.search || "";

  const handleDelete = (id: number, name: string) => {
    if (!confirm(`Delete team "${name}"? This cannot be undone.`)) return;
    deleteTeam(id, {
      onSuccess: () => toast.success("Team deleted"),
      onError: (e: any) =>
        toast.error(e.response?.data?.message || "Failed to delete"),
    });
  };

  return (
    <Layout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
        <p className="text-gray-500 mt-1">
          View and manage all user-created fantasy teams
        </p>
      </div>

      {/* Search + Stats */}
      <div className="flex items-center justify-between mb-6 gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search teams..."
            value={search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Badge className="bg-gray-100 text-gray-700 font-medium">{pagination?.total ?? teams.length} Total</Badge>
          <Badge className="bg-green-100 text-green-700 font-medium">{teams.filter((t) => !t.isLocked).length} Active</Badge>
          <Badge className="bg-red-100 text-red-700 font-medium">{teams.filter((t) => t.isLocked).length} Locked</Badge>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        {/* Table Header */}
        <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b text-xs font-semibold text-gray-500 uppercase tracking-wide">
          <div className="col-span-3">Team Name</div>
          <div className="col-span-2">Owner</div>
          <div className="col-span-1 text-center">Players</div>
          <div className="col-span-2">Formation</div>
          <div className="col-span-1 text-center">Points</div>
          <div className="col-span-1 text-center">Status</div>
          <div className="col-span-1">Created</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        {/* Rows */}
        {isLoading ? (
          [...Array(5)].map((_, i) => (
            <div
              key={i}
              className="grid grid-cols-12 gap-4 px-6 py-4 border-b animate-pulse"
            >
              {[...Array(8)].map((_, j) => (
                <div key={j} className="col-span-1 h-4 bg-gray-200 rounded" />
              ))}
            </div>
          ))
        ) : teams.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Shield className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No teams found</p>
          </div>
        ) : (
          teams.map((team: AdminTeam) => (
            <div
              key={team.id}
              className="grid grid-cols-12 gap-4 px-6 py-4 border-b last:border-0 hover:bg-gray-50 transition items-center"
            >
              {/* Name */}
              <div className="col-span-3">
                <button
                  onClick={() => setSelectedTeamId(team.id)}
                  className="font-semibold text-blue-600 hover:underline text-left flex items-center gap-1"
                >
                  {team.name}
                  <ChevronRight className="h-3 w-3" />
                </button>
              </div>

              {/* Owner */}
              <div className="col-span-2 text-sm text-gray-600">
                <p className="font-medium">{team.user?.name || "—"}</p>
                <p className="text-xs text-gray-400">
                  @{team.user?.username || "—"}
                </p>
              </div>

              {/* Players */}
              <div className="col-span-1 text-center">
                <Badge variant="outline" className="text-xs">
                  <Users className="h-3 w-3 mr-1" />
                  {team._count?.players ?? 0}
                </Badge>
              </div>

              {/* Formation */}
              <div className="col-span-2">
                <Badge className="bg-blue-50 text-blue-700 border-blue-200">
                  {team.formation || "—"}
                </Badge>
              </div>

              {/* Points */}
              <div className="col-span-1 text-center">
                <span className="font-bold text-green-600 text-sm">
                  {team.totalPoints ?? 0}
                </span>
              </div>

              {/* Status */}
              <div className="col-span-1 text-center">
                <Badge
                  className={
                    team.isLocked
                      ? "bg-red-100 text-red-700"
                      : "bg-green-100 text-green-700"
                  }
                >
                  {team.isLocked ? "🔒 Locked" : "✅ Active"}
                </Badge>
              </div>

              {/* Created */}
              <div className="col-span-1 text-xs text-gray-400">
                {new Date(team.createdAt).toLocaleDateString()}
              </div>

              {/* Actions */}
              <div className="col-span-1 flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingTeam(team)}
                  className="h-8 w-8 p-0 text-blue-500 hover:bg-blue-50"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(team.id, team.name)}
                  className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          pageSize={pagination.limit}
          onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
          onPageSizeChange={(limit) => setFilters((prev) => ({ ...prev, limit, page: 1 }))}
        />
      )}

      {/* Team Detail Drawer */}
      {selectedTeamId && (
        <TeamDetailDrawer
          teamId={selectedTeamId}
          onClose={() => setSelectedTeamId(null)}
        />
      )}

      {/* Team Edit Modal */}
      {editingTeam && (
        <TeamEditModal
          team={editingTeam}
          isSaving={isUpdating}
          onClose={() => setEditingTeam(null)}
          onSave={(data) => {
            updateTeam(
              { id: editingTeam.id, data },
              {
                onSuccess: () => {
                  toast.success("Team updated");
                  setEditingTeam(null);
                },
                onError: (e: any) =>
                  toast.error(e.response?.data?.message || "Failed to update"),
              },
            );
          }}
        />
      )}
    </Layout>
  );
}
